import React, { useRef, useState } from 'react';
import EdgePageFrame from '../EdgePageFrame';
import { animate, AnimatePresence, motion, useAnimationFrame, useMotionValue, useTransform, easeOut } from 'motion/react';
import { chevrons } from '../../../../svg/SVGData';
import { useMediaQuery } from 'react-responsive';
import useClickOutsideSentinel from '../../../../../utils/hooks/useClickOutsideSentinel';

const paths = {
    'dry': '/edge/3pc_dry/',
    'sable': '/edge/6pc_sable/',
    'synthetic': '/edge/14pc_synthetic/'
}

const dryBlurb = 'Our famous 3-piece meal!';
const sableBlurb = 'Our famous 6-piece meal!';
const syntheticBlurb = 'Our famous 14-piece meal!';


const imageNames = [
    '0_lg.jpg',
    '1_lg.jpg',
    '2_lg.jpg',
    '3_lg.jpg',
    '4_lg.jpg',
    '5_lg.jpg',
]

// type Category = 'brush';
type BrushVariety = 'dry' | 'sable' | 'synthetic';

interface BrushInfo {
    variety: BrushVariety,
    blurb: string,
    name: string
}

const allBrushes: BrushInfo[] = [
    { variety: 'dry', name: '3 piece dry', blurb: dryBlurb },
    { variety: 'sable', name: '6 piece kolinsky sable', blurb: sableBlurb },
    { variety: 'synthetic', name: '14 piece synthetic', blurb: syntheticBlurb },
]

interface chevronButton {
    viewbox: string,
    path: string,
    direction: 1 | -1,
    cursor: number,
    setCursor: React.Dispatch<number>
}

const chevronButton: React.FC<chevronButton> = ({ viewbox, path, direction, cursor, setCursor }) => {
    const x = useMotionValue(0);

    const handleOnClick = () => {
        animate(x, [24 * direction, 0], { type: 'spring' });
        setCursor(cursor + (1 * direction));
    }

    return (
        <div className='size-12 flex justify-center items-center p-2 rounded-full bg-white relative'>
            <motion.svg
                className='h-full'
                style={{ x }}
                viewBox={viewbox}
            >
                <path
                    d={path}
                    style={{ stroke: '#171717' }}
                />
            </motion.svg>
            <div
                className='absolute top-0 left-0 scale-150 bg-green-500 opacity-50 hover:cursor-pointer z-10'
                onPointerEnter={() => animate(x, 8 * direction, { type: 'spring' })}
                onPointerLeave={() => animate(x, 0, { type: 'spring' })}
                onClick={handleOnClick}
            />
        </div>
    )
}

const ImageOverlay: React.FC<{ src: string, setEmbiggen: React.Dispatch<boolean> }> = ({ src, setEmbiggen }) => {
    const imgRef = useClickOutsideSentinel(() => setEmbiggen(false));

    return (
        <motion.div
            className='fixed top-0 left-0 size-full flex justify-center items-center z-50'
        >
            <motion.img
                ref={imgRef}
                src={src}
                className='relative p-8 sm:p-16 z-50 hover:cursor-pointer shadow-2xl'
                whileHover={{ scale: 1.1 }}
            />

            <motion.div
                className='absolute top-0 left-0 size-full bg-neutral-950 z-40'
                initial={{ opacity: 0 }}
                animate={{ opacity: .5 }}
                exit={{ opacity: 0 }}
            />

        </motion.div>
    )
}

const ImageSlot: React.FC<{ imageCursor: number, varietyCursor: number }> = ({ imageCursor, varietyCursor }) => {
    const left = useMotionValue('0%');

    const previous = useRef(imageCursor);

    const getDirection = () => {
        let difference = imageCursor - previous.current;
        if (difference >= 1 || difference === -imageNames.length) {
            difference = 1;
        } else {
            difference = -1;
        }

        previous.current = imageCursor;
        return difference;
    }

    const [embiggen, setEmbiggen] = useState(false);

    return (
        <div className='size-full z-0'>
            <AnimatePresence initial={false} mode='wait'>
                <motion.img
                    key={imageCursor}
                    layout
                    src={`${paths[allBrushes[varietyCursor].variety]}${imageNames.at(imageCursor % imageNames.length)}`}
                    className='size-full object-contain hover:cursor-pointer'
                    style={{ left }}
                    initial={{ x: `${100 * getDirection()}%` }}
                    animate={{ scale: 1, x: '0%' }}
                    exit={{ x: `${100 * getDirection()}%` }}
                    whileHover={{ scale: 1.05 }}
                    onClick={() => setEmbiggen(true)}
                />
            </AnimatePresence>

            {embiggen && <ImageOverlay setEmbiggen={setEmbiggen} src={`${paths[allBrushes[varietyCursor].variety]}${imageNames.at(imageCursor % imageNames.length)}`} />}
        </div>
    )
}

interface CarouselButtonProps {
    direction: number,
    imageCursor: number,
    setImageCursor: React.Dispatch<number>
}

const CarouselButton: React.FC<CarouselButtonProps> = ({ direction, imageCursor, setImageCursor }) => {
    const rotateZ = useMotionValue(`${90 * direction * -1}deg`);

    const animationCursor = useMotionValue(0);
    const firstOpacity = useTransform(animationCursor, [1, -1, 0], [.2, .4, .1,]);
    const secondOpacity = useTransform(animationCursor, [1, -1, 0], [.4, .6, .2,]);
    const thirdOpacity = useTransform(animationCursor, [1, -1, 0], [.6, 1, .3], { ease: easeOut });

    const totalDuration = 4500;

    useAnimationFrame((t) => {
        let theta = (t / totalDuration) * (2 * Math.PI);
        animationCursor.set(Math.cos(theta));
    })

    return (
        <div
            className='flex pointer-events-auto hover:cursor-pointer'
            onClick={() => setImageCursor(imageCursor + (1 * direction))}
        >
            <div className={`size-5 sm:size-7 ${direction ? '-ml-1' : '-mr-1'}`}>
                <motion.svg
                    viewBox={chevrons['down'].viewbox}
                    className='h-full'
                    style={{ opacity: direction ? firstOpacity : thirdOpacity, rotateZ }}
                >
                    <path fill='#171717' d={chevrons['down'].path} />
                </motion.svg>
            </div>

            <div className={`size-5 sm:size-7 ${direction ? '-ml-2' : '-mr-3'}`}>
                <motion.svg
                    viewBox={chevrons['down'].viewbox}
                    className='h-full'
                    style={{ opacity: secondOpacity, rotateZ }}
                >
                    <path fill='#171717' d={chevrons['down'].path} />
                </motion.svg>
            </div>

            <div className={`size-5 sm:size-7 ${direction ? '-ml-3' : '-mr-3'}`}>
                <motion.svg
                    viewBox={chevrons['down'].viewbox}
                    className='h-full'
                    style={{ opacity: direction ? thirdOpacity : firstOpacity, rotateZ }}
                >
                    <path fill='#171717' d={chevrons['down'].path} />
                </motion.svg>
            </div>
        </div>
    )
}

interface ProductCarouselProps {
    varietyCursor: number,
    imageCursor: number,
    setImageCursor: React.Dispatch<number>
}

let ProductCarouselSmall: React.FC<ProductCarouselProps> = ({ varietyCursor, imageCursor, setImageCursor }) => {

    return (
        <div className='w-3/4 flex flex-col gap-4 mt-20 relative'>
            <div className='w-full text-center text-2xl'>{allBrushes[varietyCursor].blurb}</div>
            <div className='w-full h-96 shadow-2xl p-4 border-4 border-neutral-800 overflow-clip bg-white bg-opacity-25 relative'>
                <ImageSlot varietyCursor={varietyCursor} imageCursor={imageCursor} />
            </div>
            <div className='w-full flex justify-between'>
                <div className='w-[48%] aspect-square p-2 border-4 border-neutral-800 overflow-clip bg-white bg-opacity-25 relative'>
                    <ImageSlot varietyCursor={varietyCursor} imageCursor={imageCursor - 1} />
                </div>
                <div className='w-[48%] aspect-square p-2 border-4 border-neutral-800 overflow-clip bg-white bg-opacity-25 relative'>
                    <ImageSlot varietyCursor={varietyCursor} imageCursor={imageCursor + 1} />
                </div>
            </div>

            <div className='absolute top-0 left-0 flex items-center size-full  -ml-11 sm:-ml-16 pointer-events-none'>
                <CarouselButton imageCursor={imageCursor} setImageCursor={setImageCursor} direction={-1} />
            </div>
            <div className='absolute top-0 right-0 flex justify-end items-center  -mr-11 sm:-mr-16 size-full pointer-events-none'>
                <CarouselButton imageCursor={imageCursor} setImageCursor={setImageCursor} direction={1} />
            </div>
        </div>
    )
}

ProductCarouselSmall = React.memo(ProductCarouselSmall);

const EdgeProducts: React.FC = () => {
    const [varietyCursor, setVarietyCursor] = useState(0);
    const [imageCursor, setImageCursor] = useState(0);

    const generateOnClickHandler = (i: number) => {
        return () => {
            setVarietyCursor(i);
            setImageCursor(0);
        }
    }

    const isWide = useMediaQuery({ minWidth: 900 })
    const [menuOpen, setMenuOpen] = useState(isWide);
    const menuButtonRotateZ = useMotionValue('0deg');
    const menuButtonScale = useMotionValue(1);

    const menuButtonY = useMotionValue(0);
    const heartbeatActive = useRef(true);
    const nBeats = useRef(0);
    const beatInterval = 600; // ms
    const yRange = 16;
    useAnimationFrame((t, d) => {
        if (!heartbeatActive.current) return;

        let tBeatSteps = beatInterval * nBeats.current;
        if (t > (tBeatSteps + 1000)) {
            let direction = menuOpen ? -1 : 1;
            nBeats.current += 1
            animate(menuButtonY, [0, direction * yRange, 0], { duration: .1, ease: 'easeIn' })
        }
    })

    return (
        <EdgePageFrame className='py-0'>
            {/* Menu */}
            <div className='absolute top-0 left-0 flex justify-between w-72 p-4 pr-6 bg-white rounded-br-xl font-sans overflow-clip z-10 shadow-2xl text-nowrap'>
                <div>
                    <AnimatePresence mode='wait'>
                        {menuOpen ? (
                            <motion.div
                                key='open' className='flex flex-col gap-y-6 h-min'
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 1, transition: { duration: .6 } }}
                            >
                                <div className='flex flex-col gap-y-1'>
                                    <p className='font-bold text-xl capitalize select-none'>Brushes</p>

                                    <ul className='text-md capitalize indent-3'>
                                        {allBrushes.map((v, i) =>
                                            <li
                                                key={i}
                                                className={'hover:cursor-pointer ' + ((varietyCursor % allBrushes.length === i) ? ' font-semibold' : '')}
                                                onClick={generateOnClickHandler(i)}
                                            >
                                                {allBrushes[i].name}
                                            </li>)}
                                    </ul>
                                </div>

                                <div className='flex flex-col gap-y-1 text-neutral-500'>
                                    <p className='font-bold text-xl capitalize select-none'>Files</p>

                                    <ul className='text-md capitalize indent-3'>
                                        <li className=''> - Coming soon!</li>
                                    </ul>
                                </div>

                                <div className='flex flex-col gap-y-1 text-neutral-500'>
                                    <p className='font-bold text-xl capitalize select-none'>Bases</p>

                                    <ul className='text-md capitalize indent-3'>
                                        <li className=''> - Coming soon!</li>
                                    </ul>
                                </div>

                                <div className='flex flex-col gap-y-1 text-neutral-500'>
                                    <p className='font-bold text-xl capitalize select-none'>Snips</p>

                                    <ul className='text-md capitalize indent-3'>
                                        <li className=''> - Coming soon!</li>
                                    </ul>
                                </div>

                                <div className='flex flex-col gap-y-1 text-neutral-500'>
                                    <p className='font-bold text-xl capitalize select-none'>Razors</p>

                                    <ul className='text-md capitalize indent-3'>
                                        <li className=''> - Coming soon!</li>
                                    </ul>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                className='h-full flex justify-center items-center text-lg text-neutral-800 font-semibold capitalize'
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 1, transition: { duration: .6 } }}
                            >
                                <p>{allBrushes[varietyCursor].name}</p>
                            </motion.div>
                        )}

                    </AnimatePresence>

                </div>

                {/* Drop Menu Button */}
                <motion.div
                    className='flex justify-center items-center h-10 md:h-12 p-1 hover:cursor-pointer'
                    onPointerEnter={() => {
                        animate(menuButtonScale, 1.1, { duration: .1 });
                        heartbeatActive.current = false;
                    }}
                    onPointerLeave={() => animate(menuButtonScale, 1, { duration: .1 })}
                    onClick={() => {
                        animate(menuButtonRotateZ, !menuOpen ? '-180deg' : '0deg')
                        setMenuOpen(!menuOpen);
                    }}
                >
                    <motion.svg
                        className='h-full'
                        style={{ rotateZ: menuButtonRotateZ, scale: menuButtonScale, y: menuButtonY }}
                        viewBox={chevrons['down'].viewbox}
                    >
                        <path
                            fill='#171717'
                            stroke='black'
                            strokeWidth={12}
                            d={chevrons['down'].path}
                        >

                        </path>
                    </motion.svg>
                </motion.div>
            </div>

            {/* Carousel */}
            <ProductCarouselSmall varietyCursor={varietyCursor} imageCursor={imageCursor} setImageCursor={setImageCursor} />
            <div className='text-lg text-red-800 text-center mt-3 leading-tight w-1/2'>* Note: Some images may not match product! More photography coming soon!</div>
        </EdgePageFrame>
    )
}

export default EdgeProducts;